import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductsFilters, productsMultipleFilters } from '@interfaces/products.interface';
import {
  Accordion,
  ActionIcon,
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Pill,
  Radio,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconTrash, IconX } from '@tabler/icons-react';

import useStateWithDebounce from '@hooks/useStateWithDebouce';

interface AccordionFilterItemProps {
  filterKey: ProductsFilters;
  title: string;
  selectedValues: string[];
  changeSelectedValues: (values: string[]) => void;
  loadMoreValues: () => void;
  hasMoreValues: boolean;
  values: { name: string; count: number }[];
  isFetching: boolean;
  setFilterSearchQuery: (filterSearchQuery: string) => void;
}

export default function AccordionFilterItem({
  filterKey,
  title,
  selectedValues,
  changeSelectedValues,
  loadMoreValues,
  hasMoreValues,
  values,
  isFetching,
  setFilterSearchQuery,
}: AccordionFilterItemProps) {
  const { t } = useTranslation();

  // ==========================================================================
  // State
  // ==========================================================================
  const [filterSearch, setFilterSearch, debouncedFilterSearch] =
    useStateWithDebounce<string>('');

  useEffect(() => {
    setFilterSearchQuery(debouncedFilterSearch);
  }, [debouncedFilterSearch]);

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const toggleFilter = useCallback(
    (filterValue: string) => {
      if (productsMultipleFilters.includes(filterKey)) {
        if (selectedValues.includes(filterValue)) {
          changeSelectedValues(
            selectedValues.filter((value) => value !== filterValue),
          );
        } else {
          changeSelectedValues([...selectedValues, filterValue]);
        }
      } else {
        if (selectedValues.includes(filterValue)) {
          changeSelectedValues([]);
        } else {
          changeSelectedValues([filterValue]);
        }
      }
    },
    [changeSelectedValues, selectedValues],
  );

  const clearFilter = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation(); // To block accordion click

      changeSelectedValues([]);
    },
    [changeSelectedValues, selectedValues],
  );

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Accordion.Item value={filterKey}>
      <Accordion.Control>
        <Group justify="space-between" wrap="nowrap" align="center">
          <Group justify="start" gap="xs">
            <Text c="#444">{title}</Text>
          </Group>

          <Group gap="xs">
            {selectedValues.length > 0 && (
              <Text span fs="italic" fz="sm">
                {t('search.selectedFiltersCount', {
                  count: selectedValues.length,
                })}
              </Text>
            )}

            {selectedValues.length > 0 && (
              <ActionIcon
                component="div"
                variant="transparent"
                onClick={clearFilter}
              >
                <IconTrash
                  size={20}
                  color="var(--mantine-primary-color-filled)"
                />
              </ActionIcon>
            )}
          </Group>
        </Group>
      </Accordion.Control>
      <Accordion.Panel pos="relative">
        <LoadingOverlay visible={isFetching} />
        <TextInput
          placeholder={t('search.searchFilter')}
          mb="md"
          value={filterSearch}
          onChange={(e) => setFilterSearch(e.currentTarget.value)}
          rightSection={
            filterSearch &&
            filterSearch?.trim() !== '' && (
              <Tooltip label={t('search.clearSearch')}>
                <ActionIcon
                  variant="subtle"
                  onClick={() => setFilterSearch('')}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Tooltip>
            )
          }
        />
        {selectedValues.length > 0 && (
          <>
            <Text size="sm" mb="xs" fs="italic">
              {t('search.selectedFilters')}
            </Text>
            <Group gap="xs">
              {selectedValues.toSorted().map((value) => (
                <Pill
                  key={value}
                  withRemoveButton
                  onRemove={() => toggleFilter(value)}
                >
                  {value}
                </Pill>
              ))}
            </Group>
            <Divider my="md" />
          </>
        )}
        {values.length > 0 ? (
          <Stack gap="xs">
            {productsMultipleFilters.includes(filterKey) ? (
              values.map(({ name /*, count */ }) => (
                <Checkbox
                  key={name}
                  label={
                    <Text size="sm">
                      {name}{' '}
                      {/* <Text span c="brand">
                        ({count})
                      </Text> */}
                    </Text>
                  }
                  checked={selectedValues.includes(name)}
                  onChange={() => toggleFilter(name)}
                />
              ))
            ) : (
              <Radio.Group
                value={selectedValues[0] || null}
                onChange={toggleFilter}
              >
                {values.map(({ name /*, count */ }) => (
                  <Radio
                    key={name}
                    label={
                      <Text size="sm">
                        {name}{' '}
                        {/* <Text span c="brand">
                          ({count})
                        </Text> */}
                      </Text>
                    }
                    name={filterKey}
                    value={name}
                    mb="xs"
                  />
                ))}
              </Radio.Group>
            )}
            {hasMoreValues && (
              <Button onClick={loadMoreValues} loading={isFetching}>
                {t('search.loadMoreValues')}
              </Button>
            )}
          </Stack>
        ) : (
          <Text size="sm" c="#444">
            {isFetching ? t('search.loading') : t('search.noResultsForFilter')}
          </Text>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}
